import { AuditReportGrain } from "../../models/auditReports";
import { REPORT_MAX_RANGE_MONTHS } from "./report-date-range";

/**
 * Rangos predefinidos del ticket, resueltos en el front: solo setean
 * dateFrom/dateTo y viajan por el mismo contrato que un rango a mano, así que la
 * API no necesita un segundo modo de decir lo mismo ni tiene que adivinar cuál
 * es "hoy" para el usuario.
 *
 * Los presets caen sobre PERÍODOS COMPLETOS y cerrados, no sobre ventanas
 * móviles. Un "último mes" de tipo "los últimos 31 días" en un reporte agrupado
 * por mes devuelve dos filas partidas (los últimos días de un mes y los
 * primeros del otro), y esas filas parecen una caída de la métrica cuando en
 * realidad son medio mes cada una. Con períodos completos, cada fila del reporte
 * es un mes o una semana de verdad.
 */
export type AuditReportPresetKey = "last_week" | "last_month" | "last_6_months";

/**
 * Cuarta opción de la fila de períodos. No es un preset: no resuelve un rango,
 * abre los selectores de fecha desde/hasta. Mientras no esté elegida, esos
 * selectores no se muestran — la fila de botones cubre el caso normal y las
 * fechas a mano son la excepción.
 */
export const CUSTOM_PERIOD = "custom" as const;

export type AuditReportPeriodSelection = AuditReportPresetKey | typeof CUSTOM_PERIOD;

export const CUSTOM_PERIOD_LABEL = "Personalizado";

export const CUSTOM_PERIOD_DESCRIPTION =
  "Elegí fecha desde y hasta a mano, hasta 6 meses";

export interface AuditReportPreset {
  key: AuditReportPresetKey;
  label: string;
  /** Qué rango y qué agrupamiento deja, para el tooltip del botón. */
  description: string;
  /**
   * Grano con el que se consulta al apretar este preset.
   *
   * No hay control de grano en la pantalla: el botón define el rango Y el
   * agrupamiento juntos, que es lo único que hace que los tres tengan sentido.
   * "Última semana" agrupado por mes daría una sola fila rotulada con un mes y
   * una semana de datos adentro, que se lee como un derrumbe; y nadie pide "los
   * últimos 6 meses" para recibir 26 filas semanales por defecto.
   *
   * Para un rango a mano no hay preset, y ahí se mantiene el último grano usado.
   */
  grain: AuditReportGrain;
}

export const AUDIT_REPORT_PRESETS: AuditReportPreset[] = [
  {
    key: "last_week",
    label: "Última semana",
    description: "La última semana completa, de lunes a domingo",
    grain: "week",
  },
  {
    key: "last_month",
    label: "Último mes",
    description: "El último mes calendario completo",
    grain: "month",
  },
  {
    key: "last_6_months",
    label: `Últimos ${REPORT_MAX_RANGE_MONTHS} meses`,
    description: `Los últimos ${REPORT_MAX_RANGE_MONTHS} meses calendario completos, mes por mes`,
    grain: "month",
  },
];

/**
 * Presets que se muestran para un reporte.
 *
 * Se filtran, no se deshabilitan: un botón gris sin control de grano al lado no
 * tiene forma de explicarse. Hoy el único que se cae es "última semana" en
 * Recurrencia, que se calcula por mes por definición ("2+ eventos distintos en
 * el mismo mes"), así que ofrecer un rango semanal ahí no significa nada.
 */
export const presetsForReport = (reportGrains: AuditReportGrain[]): AuditReportPreset[] =>
  AUDIT_REPORT_PRESETS.filter((preset) => reportGrains.includes(preset.grain));

/** Preset con el que arranca la pantalla, dado los granos que soporta el reporte. */
export const initialPresetFor = (reportGrains: AuditReportGrain[]): AuditReportPreset => {
  const available = presetsForReport(reportGrains);
  return available.find((preset) => preset.key === "last_month") ?? available[0];
};

const startOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const addDays = (date: Date, days: number): Date => {
  const result = startOfDay(date);
  result.setDate(result.getDate() + days);
  return result;
};

/** Lunes de la semana ISO a la que pertenece `date`. */
const startOfIsoWeek = (date: Date): Date => {
  const result = startOfDay(date);
  // getDay() es 0 para domingo: en semana ISO el domingo es el día 7.
  const isoDay = result.getDay() === 0 ? 7 : result.getDay();
  return addDays(result, 1 - isoDay);
};

const startOfMonth = (date: Date, monthsAgo = 0): Date => {
  const result = startOfDay(date);
  result.setDate(1);
  result.setMonth(result.getMonth() - monthsAgo);
  return result;
};

/** Último día del mes anterior al de `date`. */
const endOfPreviousMonth = (date: Date): Date => addDays(startOfMonth(date), -1);

/**
 * Rango del preset. `today` se inyecta para que sea testeable y para que el
 * "hoy" sea siempre el del navegador del usuario, no el del servidor.
 */
export const resolveAuditReportPreset = (
  preset: AuditReportPresetKey,
  today: Date
): { dateFrom: Date; dateTo: Date } => {
  if (preset === "last_week") {
    const thisWeekMonday = startOfIsoWeek(today);
    return { dateFrom: addDays(thisWeekMonday, -7), dateTo: addDays(thisWeekMonday, -1) };
  }

  if (preset === "last_month") {
    return { dateFrom: startOfMonth(today, 1), dateTo: endOfPreviousMonth(today) };
  }

  // Los 6 meses cerrados anteriores al actual. Entra justo en el tope de 6 meses
  // que valida el backend: del 1 del mes -6 al último día del mes -1.
  return {
    dateFrom: startOfMonth(today, REPORT_MAX_RANGE_MONTHS),
    dateTo: endOfPreviousMonth(today),
  };
};
